import React, { useMemo } from 'react';
import { View, Text } from '../lib/react-native-compat';
import { selectedJobAtom, jobUserDataAtom, jobCostDetailAtom, jobPhaseCodesAtom, selectedVersionAtom, jobCostDetailCondensedAtom } from '../store/company';
import { useAtom } from 'jotai';
import { pNum } from '../db/company';
import { Bar } from 'react-chartjs-2';
import { SERIES, alpha } from '../styles/chartColors';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const normCC = (cc) => (cc && /^\d{6}$/.test(cc)) ? cc : '100101';

const EarnedVsBurned = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailCondensedAtom);
    const [jobPhaseCodes] = useAtom(jobPhaseCodesAtom);

    const chartData = useMemo(() => {
        if (!jobUserData || !jobCostDetail) return null;

        // Deduplicate user data (same logic as overviewNew)
        const grouped = jobUserData.reduce((acc, item) => {
            const key = `${item.Version_GUID}_${item.BU_ID}_${item.Forecast_Tab}_${item.Forecast_Field_Name}_${item.Forecast_Row_ID}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
        }, {});
        const dedup = Object.values(grouped).map(group => group.reduce((latest, current) => {
            const ld = new Date(Math.max(new Date(latest.DSS_Created_Date), new Date(latest.DSS_Updated_Datetime || 0)));
            const cd = new Date(Math.max(new Date(current.DSS_Created_Date), new Date(current.DSS_Updated_Datetime || 0)));
            return cd > ld ? current : latest;
        }));

        const dayZeroHrsByCode = {};
        const costCodeQuantities = {};
        const coHrsByCode = {};
        const coQtyByCode = {};
        const installedQtyByCode = {};
        const plugQtyByCode = {};

        dedup.forEach(d => {
            if (d.Version_GUID !== selectedVersion) return;
            if (d.Forecast_Tab === 'Day Zero Budget' && d.Cost_Code && d.Cost_Code !== '1000' && d.Cost_Code !== '990000') {
                const isRev = d.Cost_Type?.toString().startsWith('4');
                if (!isRev) {
                    const key = normCC(d.Cost_Code);
                    dayZeroHrsByCode[key] = (dayZeroHrsByCode[key] || 0) + (parseFloat(d.Hours) || 0);
                    costCodeQuantities[key] = (costCodeQuantities[key] || 0) + pNum(d.Quantity || 0);
                }
            }
            if (d.Forecast_Tab === 'Change Order' && d.Cost_Code && d.Cost_Code !== '1000' && d.Cost_Code !== '990000') {
                const isRev = d.Cost_Type?.toString().startsWith('4');
                if (!isRev) {
                    const key = normCC(d.Cost_Code);
                    coHrsByCode[key] = (coHrsByCode[key] || 0) + (parseFloat(d.Hours) || 0);
                    coQtyByCode[key] = (coQtyByCode[key] || 0) + (parseFloat(d.Quantity) || 0);
                }
            }
            if (d.Forecast_Tab === 'Scorecard') {
                if (d.Forecast_Field_Name === 'installed') installedQtyByCode[normCC(d.Forecast_Row_ID)] = pNum(d.Forecast_Value) || 0;
                if (d.Forecast_Field_Name === 'plug') plugQtyByCode[normCC(d.Forecast_Row_ID)] = pNum(d.Forecast_Value) || 0;
            }
        });

        // Actual hours from jobCostDetail (labor cost types 51xxx, excluding 518xx)
        const actualHoursByCode = {};
        const heavyJobInstalledByCode = {};
        if (Array.isArray(jobCostDetail)) {
            jobCostDetail.forEach(item => {
                if (item.Phase_Code && item.Cost_Type_Code?.[0] === '5' && item.Cost_Type_Code?.[1] === '1' && item.Cost_Type_Code?.[2] !== '8' && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                    const npc = normCC(item.Phase_Code);
                    actualHoursByCode[npc] = (actualHoursByCode[npc] || 0) + (pNum(item.Total_Hours) || 0);
                }
                if (item.Document_Type === 'FP' && item.Phase_Code) {
                    const npc = normCC(item.Phase_Code);
                    heavyJobInstalledByCode[npc] = (heavyJobInstalledByCode[npc] || 0) + (pNum(item.Total_Hours) || 0);
                }
            });
        }

        // Collect all phase codes that have any data
        const allCodes = new Set([
            ...Object.keys(dayZeroHrsByCode),
            ...Object.keys(actualHoursByCode),
        ]);
        // Filter out special codes
        ['100101'].forEach(c => allCodes.delete(c));

        const phases = [];
        allCodes.forEach(cc => {
            const budgetHrs = (dayZeroHrsByCode[cc] || 0) + (coHrsByCode[cc] || 0);
            const dzQty = costCodeQuantities[cc] || 0;
            const totalQty = dzQty + (coQtyByCode[cc] || 0) + (plugQtyByCode[cc] || 0);
            const installed = (installedQtyByCode[cc] || 0) + (heavyJobInstalledByCode[cc] || 0);
            const pctComplete = totalQty > 0 ? Math.min(installed / totalQty, 1) : 0;
            const earnedHrs = pctComplete * budgetHrs;
            const actualHrs = actualHoursByCode[cc] || 0;

            if (earnedHrs === 0 && actualHrs === 0) return;

            const desc = jobPhaseCodes?.[cc] || cc;
            phases.push({ code: cc, description: desc, earnedHrs, actualHrs });
        });

        // Sort descending by max(earned, actual) so biggest phases are on top
        phases.sort((a, b) => Math.max(b.earnedHrs, b.actualHrs) - Math.max(a.earnedHrs, a.actualHrs));

        return {
            labels: phases.map(p => p.description),
            datasets: [
                {
                    label: 'Earned Hours',
                    data: phases.map(p => Math.round(p.earnedHrs)),
                    backgroundColor: alpha(SERIES[0], 0.85),
                    borderColor: SERIES[0],
                    borderWidth: 1,
                },
                {
                    label: 'Burned Hours',
                    data: phases.map(p => Math.round(p.actualHrs)),
                    backgroundColor: alpha(SERIES[1], 0.85),
                    borderColor: SERIES[1],
                    borderWidth: 1,
                },
            ],
        };
    }, [jobUserData, jobCostDetail, jobPhaseCodes, selectedVersion]);

    if (!chartData || chartData.labels.length === 0) {
        return (
            <View style={{ padding: 40, alignItems: 'center' }}>
                <Text style={{ fontSize: 14, color: '#6b7280' }}>No hours data available for this job.</Text>
            </View>
        );
    }

    const chartHeight = Math.max(400, chartData.labels.length * 28);

    return (
        <View style={{ padding: 20, maxWidth: 900 }}>
            <View style={{ height: chartHeight }}>
                <Bar
                    data={chartData}
                    options={{
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            title: {
                                display: true,
                                text: 'Earned Hours vs Burned Hours by Phase',
                                font: { size: 14, weight: 'bold' },
                            },
                            legend: {
                                position: 'top',
                            },
                            tooltip: {
                                callbacks: {
                                    label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}`,
                                },
                            },
                        },
                        scales: {
                            x: {
                                title: { display: true, text: 'Hours' },
                                ticks: {
                                    callback: (v) => v.toLocaleString(),
                                },
                            },
                            y: {
                                ticks: { font: { size: 11 } },
                            },
                        },
                    }}
                />
            </View>
        </View>
    );
};

export default EarnedVsBurned;
